import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

interface ReservationFormProps {
  selectedStoreId: string;
  selectedStore: string;
  selectedDate: string;
  selectedTime: string;
  onClose: () => void;
}

export default function ReservationForm({
  selectedStoreId,
  selectedStore,
  selectedDate,
  selectedTime,
  onClose,
}: ReservationFormProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [storeHours, setStoreHours] = useState<{ opening_time: string; closing_time: string } | null>(null);
  const [formData, setFormData] = useState({
    customerName: '',
    phoneNumber: '',
    kakaoId: '',
    telegramId: '',
    date: selectedDate,
    time: selectedTime,
  });

  useEffect(() => {
    fetchStoreHours();
  }, [selectedStoreId]);

  const fetchStoreHours = async () => {
    try {
      const { data, error } = await supabase
        .from('stores')
        .select('opening_time, closing_time')
        .eq('id', selectedStoreId)
        .single();

      if (error) throw error;
      setStoreHours(data);
    } catch (error) {
      console.error('매장 영업시간 조회 실패:', error);
    }
  };

  // 매장별 시간 옵션 생성
  const generateTimeOptions = () => {
    if (!storeHours) return [];

    const times = [];
    const opening = storeHours.opening_time;
    const closing = storeHours.closing_time;

    // 24시간 영업 (00:00 ~ 23:59)
    if (opening === '00:00' && closing === '23:59') {
      for (let hour = 0; hour <= 23; hour++) {
        times.push(`${hour.toString().padStart(2, '0')}:00`);
        times.push(`${hour.toString().padStart(2, '0')}:30`);
      }
      return times;
    }

    // 일반 영업시간
    const [openHour, openMin] = opening.split(':').map(Number);
    const [closeHour, closeMin] = closing.split(':').map(Number);

    // 여의도 시그니엘 (18:00 ~ 00:00)
    if (openHour >= 18 && closeHour === 0) {
      // 18:00부터 23:30까지
      for (let hour = openHour; hour <= 23; hour++) {
        times.push(`${hour.toString().padStart(2, '0')}:00`);
        times.push(`${hour.toString().padStart(2, '0')}:30`);
      }
      return times;
    }

    // 기본 로직 (일반적인 경우)
    let currentHour = openHour;
    let currentMin = openMin;

    while (currentHour < closeHour || (currentHour === closeHour && currentMin < closeMin)) {
      times.push(`${currentHour.toString().padStart(2, '0')}:${currentMin.toString().padStart(2, '0')}`);
      
      currentMin += 30;
      if (currentMin >= 60) {
        currentMin = 0;
        currentHour++;
      }
    }

    return times;
  };

  const timeOptions = generateTimeOptions();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.customerName) {
      alert('이름을 입력해주세요');
      return;
    }

    if (!formData.phoneNumber && !formData.kakaoId && !formData.telegramId) {
      alert('전화번호, 카카오톡 아이디, 텔레그램 아이디 중 최소 하나는 입력해주세요');
      return;
    }

    setLoading(true);

    try {
      // 연락 방법 결정
      let contactMethod = '';
      let contactInfo = '';
      
      if (formData.phoneNumber) {
        contactMethod = 'phone';
        contactInfo = formData.phoneNumber;
      } else if (formData.kakaoId) {
        contactMethod = 'kakao';
        contactInfo = formData.kakaoId;
      } else if (formData.telegramId) {
        contactMethod = 'telegram';
        contactInfo = formData.telegramId;
      }

      const { error } = await supabase.from('reservations').insert({
        store_id: selectedStoreId,
        store: selectedStore,
        customer_name: formData.customerName,
        reservation_date: formData.date,
        reservation_time: formData.time,
        contact_method: contactMethod,
        contact_info: contactInfo,
        kakao_id: formData.kakaoId || null,
        telegram_id: formData.telegramId || null,
        user_id: user?.id || null,
      });

      if (error) throw error;

      // 텔레그램 알림 전송
      try {
        const notificationData = {
          reservation: {
            store_name: selectedStore,
            customer_name: formData.customerName,
            reservation_date: formData.date,
            reservation_time: formData.time,
            phone_number: formData.phoneNumber,
            kakao_id: formData.kakaoId,
            telegram_id: formData.telegramId,
            created_at: new Date().toISOString(),
          }
        };

        await fetch(
          `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/telegram-notification`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify(notificationData),
          }
        );
      } catch (notificationError) {
        console.error('알림 전송 실패:', notificationError);
        // 알림 실패해도 예약은 성공으로 처리
      }

      alert('예약이 완료되었습니다!');
      onClose();
    } catch (error) {
      console.error('예약 실패:', error);
      alert('예약에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  const getStoreHoursText = () => {
    if (!storeHours) return '';
    
    const { opening_time, closing_time } = storeHours;
    
    if (opening_time === '00:00' && closing_time === '23:59') {
      return '24시간 영업';
    }
    
    return `${opening_time} ~ ${closing_time}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <h2 className="text-2xl font-bold text-gray-900 mb-6">예약 정보 입력</h2>

        <div className="bg-teal-50 rounded-lg p-4 mb-6">
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">매장</span>
              <span className="font-medium text-gray-900">{selectedStore}</span>
            </div>
            {storeHours && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600">영업시간</span>
                <span className="font-medium text-gray-900">{getStoreHoursText()}</span>
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              이름 *
            </label>
            <input
              type="text"
              value={formData.customerName}
              onChange={(e) =>
                setFormData({ ...formData, customerName: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="홍길동"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              예약 날짜
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) =>
                setFormData({ ...formData, date: e.target.value })
              }
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              예약 시간
            </label>
            <select
              value={formData.time}
              onChange={(e) =>
                setFormData({ ...formData, time: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              required
            >
              {timeOptions.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              전화번호
            </label>
            <input
              type="tel"
              value={formData.phoneNumber}
              onChange={(e) =>
                setFormData({ ...formData, phoneNumber: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="010-1234-5678"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              카카오톡 아이디
            </label>
            <input
              type="text"
              value={formData.kakaoId}
              onChange={(e) =>
                setFormData({ ...formData, kakaoId: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="카카오톡 아이디"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              텔레그램 아이디
            </label>
            <input
              type="text"
              value={formData.telegramId}
              onChange={(e) =>
                setFormData({ ...formData, telegramId: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="@telegram_id"
            />
          </div>

          <p className="text-xs text-gray-500">
            * 전화번호, 카카오톡 아이디, 텔레그램 아이디 중 최소 하나는 입력해주세요
          </p>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {loading ? '예약 중...' : '예약 완료'}
          </button>
        </form>
      </div>
    </div>
  );
}